import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Calendar, MessageCircle, Download, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
type PriceItem = {
  name: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  price: string;
};
type Category = {
  id: string;
  anchorId: string;
  title: {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  items: PriceItem[];
};
function AccordionItem({
  category,
  isOpen,
  onToggle,
  lang,
  isHighlighted
}: {
  category: Category;
  isOpen: boolean;
  onToggle: () => void;
  lang: 'en' | 'ua' | 'ru' | 'ro';
  isHighlighted?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
      const isMobile = window.innerWidth < 1024;
      if (isMobile && headerRef.current) {
        setTimeout(() => {
          const el = headerRef.current;
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });
          }
        }, 150);
      }
    } else {
      setHeight(0);
    }
  }, [isOpen]);
  return <div id={category.anchorId} className={`border-b border-gray-100 last:border-b-0 transition-all duration-500 rounded-lg ${isHighlighted ? 'ring-2 ring-teal-500 bg-teal-50/50' : ''}`} data-id="element-2099">
      <button ref={headerRef} onClick={onToggle} className="w-full flex items-center justify-between min-h-[56px] py-4 px-2 text-left cursor-pointer select-none" aria-expanded={isOpen} data-id="element-2100">
        <span className={`text-[15px] font-semibold transition-colors duration-200 ${isOpen ? 'text-teal-600' : 'text-gray-800'}`} data-id="element-2101">
          {category.title[lang]}
        </span>
        <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-250 ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2102">
          {isOpen ? <Minus className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-2103" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-2104" />}
        </span>
      </button>

      <div style={{
      maxHeight: isOpen ? `${height}px` : '0px'
    }} className="overflow-hidden transition-[max-height] duration-250 ease-[cubic-bezier(0.4,0,0.2,1)]" data-id="element-2105">
        <div ref={contentRef} className={`pb-6 px-2 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`} data-id="element-2106">
          <div className="space-y-3.5" data-id="element-2107">
            {category.items.map((item, idx) => <div key={idx} className="flex items-baseline gap-2" data-id="element-2108">
                <span className="text-[13px] text-gray-600 leading-snug" data-id="element-2109">
                  {item.name[lang]}
                </span>
                <span className="flex-grow border-b border-dotted border-teal-200 min-w-[16px] relative top-[-3px]" data-id="element-2110" />
                <span className="text-[13px] font-bold text-teal-700 whitespace-nowrap tabular-nums" data-id="element-2111">
                  {item.price}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
export function PricesPage() {
  const {
    t,
    lang
  } = useTranslation();
  const location = useLocation();
  const [openId, setOpenId] = useState<string | null>('consultations');
  const [highlightedAnchor, setHighlightedAnchor] = useState<string | null>(null);
  const hasScrolledRef = useRef(false);
  const toggle = useCallback((id: string) => {
    setOpenId(prev => prev === id ? null : id);
  }, []);
  const categories: Category[] = [{
    id: 'consultations',
    anchorId: 'consultations',
    title: {
      en: 'Consultations',
      ua: 'Консультації',
      ru: 'Консультации',
      ro: 'Consultații'
    },
    items: [{
      name: {
        en: 'New Patient Exam (incl. 2 X-Rays)',
        ua: 'Огляд нового пацієнта (вкл. 2 знімки)',
        ru: 'Осмотр нового пациента (вкл. 2 снимка)',
        ro: 'Examinare Pacient Nou (incl. 2 Radiografii)'
      },
      price: '£60'
    }, {
      name: {
        en: 'Routine Check Up',
        ua: 'Плановий огляд',
        ru: 'Плановый осмотр',
        ro: 'Control de Rutină'
      },
      price: '£50'
    }, {
      name: {
        en: 'Emergency Appointment',
        ua: 'Екстрений прийом',
        ru: 'Экстренный прием',
        ro: 'Consultație de Urgență'
      },
      price: '£75'
    }, {
      name: {
        en: 'Orthodontics Consultation',
        ua: 'Консультація ортодонта',
        ru: 'Консультация ортодонта',
        ro: 'Consultație Ortodontică'
      },
      price: '£60'
    }]
  }, {
    id: 'hygiene',
    anchorId: 'dental-hygiene',
    title: {
      en: 'Dental Hygiene',
      ua: 'Гігієна',
      ru: 'Гигиена',
      ro: 'Igienă Dentară'
    },
    items: [{
      name: {
        en: 'Scale & Polish',
        ua: 'Чистка та полірування',
        ru: 'Чистка и полировка',
        ro: 'Detartraj și Periaj'
      },
      price: '£95'
    }, {
      name: {
        en: 'Airflow Stain Removal',
        ua: 'Видалення нальоту Airflow',
        ru: 'Удаление налета Airflow',
        ro: 'Îndepărtarea Petelor cu Airflow'
      },
      price: '£45'
    }, {
      name: {
        en: 'Scale & Polish + Airflow + Whitening',
        ua: 'Чистка + Airflow + відбілювання',
        ru: 'Чистка + Airflow + отбеливание',
        ro: 'Detartraj + Airflow + Albire'
      },
      price: '£350'
    }, {
      name: {
        en: 'Deep Cleaning (per quadrant)',
        ua: 'Глибока чистка (1 квадрант)',
        ru: 'Глубокая чистка (1 квадрант)',
        ro: 'Curățare Profundă (per cadran)'
      },
      price: '£190'
    }, {
      name: {
        en: 'Curettage (1–3 teeth)',
        ua: 'Кюретаж (1–3 зуби)',
        ru: 'Кюретаж (1–3 зуба)',
        ro: 'Chiuretaj (1–3 dinți)'
      },
      price: '£60–80'
    }]
  }, {
    id: 'general',
    anchorId: 'fillings-restorations',
    title: {
      en: 'Fillings & Restorations',
      ua: 'Пломби та реставрації',
      ru: 'Пломбы и реставрации',
      ro: 'Plombe și Restaurări'
    },
    items: [{
      name: {
        en: 'Composite Filling (small)',
        ua: 'Композитна пломба (мала)',
        ru: 'Композитная пломба (малая)',
        ro: 'Plombă Compozită (mică)'
      },
      price: '£120–150'
    }, {
      name: {
        en: 'Composite Filling (medium)',
        ua: 'Композитна пломба (середня)',
        ru: 'Композитная пломба (средняя)',
        ro: 'Plombă Compozită (medie)'
      },
      price: '£150–180'
    }, {
      name: {
        en: 'Composite Filling (large)',
        ua: 'Композитна пломба (велика)',
        ru: 'Композитная пломба (большая)',
        ro: 'Plombă Compozită (mare)'
      },
      price: '£180–250'
    }, {
      name: {
        en: 'Composite Bonding',
        ua: 'Композитний бондинг',
        ru: 'Композитный бондинг',
        ro: 'Bonding Compozit'
      },
      price: '£250'
    }, {
      name: {
        en: 'Tooth Restoration / Build up',
        ua: 'Реставрація / нарощування зуба',
        ru: 'Реставрация / наращивание зуба',
        ro: 'Restaurare / Reconstituire Dinte'
      },
      price: '£200'
    }, {
      name: {
        en: 'Fiber Post',
        ua: 'Скловолоконний штифт',
        ru: 'Стекловолоконный штифт',
        ro: 'Pivot din Fibră'
      },
      price: '£150'
    }, {
      name: {
        en: 'Temporary Filling',
        ua: 'Тимчасова пломба',
        ru: 'Временная пломба',
        ro: 'Plombă Provizorie'
      },
      price: '£50'
    }, {
      name: {
        en: 'Re-cement Crown (each)',
        ua: 'Цементування коронки (1 шт.)',
        ru: 'Цементирование коронки (1 шт.)',
        ro: 'Recimentare Coroană'
      },
      price: '£80–119'
    }]
  }, {
    id: 'children',
    anchorId: 'children',
    title: {
      en: "Children's Dental Treatment",
      ua: 'Дитяча стоматологія',
      ru: 'Детская стоматология',
      ro: 'Tratament Dentar Pediatric'
    },
    items: [{
      name: {
        en: 'Routine Dental Examination',
        ua: 'Плановий огляд',
        ru: 'Плановый осмотр',
        ro: 'Examinare Dentară de Rutină'
      },
      price: '£60'
    }, {
      name: {
        en: 'Dental Hygiene / Cleaning (child)',
        ua: 'Гігієна / чистка (дитина)',
        ru: 'Гигиена / чистка (ребенок)',
        ro: 'Igienă / Curățare (copil)'
      },
      price: '£65'
    }, {
      name: {
        en: 'Fluoride Application',
        ua: 'Фторування',
        ru: 'Фторирование',
        ro: 'Aplicare Fluor'
      },
      price: '£25'
    }, {
      name: {
        en: 'Fissure Sealant (per tooth)',
        ua: 'Герметизація фісур (1 зуб)',
        ru: 'Герметизация фиссур (1 зуб)',
        ro: 'Sigilare Fisuri'
      },
      price: '£50'
    }, {
      name: {
        en: 'White Filling (baby tooth)',
        ua: 'Біла пломба (молочний зуб)',
        ru: 'Белая пломба (молочный зуб)',
        ro: 'Plombă Albă (dinte de lapte)'
      },
      price: '£80–120'
    }, {
      name: {
        en: 'Simple Extraction (baby / primary tooth)',
        ua: 'Просте видалення (молочний зуб)',
        ru: 'Простое удаление (молочный зуб)',
        ro: 'Extracție Simplă (dinte de lapte)'
      },
      price: '£50–120'
    }, {
      name: {
        en: 'Pulpotomy',
        ua: 'Пульпотомія',
        ru: 'Пульпотомия',
        ro: 'Pulpotomie'
      },
      price: '£180–200'
    }, {
      name: {
        en: 'Root Canal Treatment (baby tooth)',
        ua: 'Лікування каналів (молочний зуб)',
        ru: 'Лечение каналов (молочный зуб)',
        ro: 'Tratament de Canal (dinte de lapte)'
      },
      price: '£200–250'
    }]
  }, {
    id: 'root-canal',
    anchorId: 'root-canal',
    title: {
      en: 'Root Canal Treatment',
      ua: 'Лікування каналів',
      ru: 'Лечение каналов',
      ro: 'Tratament de Canal'
    },
    items: [{
      name: {
        en: 'Incisor',
        ua: 'Різець',
        ru: 'Резец',
        ro: 'Incisiv'
      },
      price: '£400'
    }, {
      name: {
        en: 'Premolar',
        ua: 'Премоляр',
        ru: 'Премоляр',
        ro: 'Premolar'
      },
      price: '£500'
    }, {
      name: {
        en: 'Molar',
        ua: 'Моляр',
        ru: 'Моляр',
        ro: 'Molar'
      },
      price: '£600'
    }, {
      name: {
        en: 'Re-Root (Incisor)',
        ua: 'Перелікування (різець)',
        ru: 'Перелечивание (резец)',
        ro: 'Retratament Canal (Incisiv)'
      },
      price: '£600'
    }, {
      name: {
        en: 'Re-Root (Premolar)',
        ua: 'Перелікування (премоляр)',
        ru: 'Перелечивание (премоляр)',
        ro: 'Retratament Canal (Premolar)'
      },
      price: '£750'
    }, {
      name: {
        en: 'Re-Root (Molar)',
        ua: 'Перелікування (моляр)',
        ru: 'Перелечивание (моляр)',
        ro: 'Retratament Canal (Molar)'
      },
      price: '£850'
    }]
  }, {
    id: 'cosmetic',
    anchorId: 'whitening',
    title: {
      en: 'Whitening & Cosmetic',
      ua: 'Відбілювання та естетика',
      ru: 'Отбеливание и эстетика',
      ro: 'Albire și Estetică'
    },
    items: [{
      name: {
        en: 'Clinical Teeth Whitening',
        ua: 'Клінічне відбілювання',
        ru: 'Клиническое отбеливание',
        ro: 'Albire Dentară Clinică'
      },
      price: '£280'
    }, {
      name: {
        en: 'Home Whitening Kit',
        ua: 'Домашній набір для відбілювання',
        ru: 'Домашний набор для отбеливания',
        ro: 'Kit de Albire la Domiciliu'
      },
      price: '£275'
    }, {
      name: {
        en: 'Intracanal Bleaching (1 tooth)',
        ua: 'Внутрішньо-канальне відбілювання (1 зуб)',
        ru: 'Внутриканальное отбеливание (1 зуб)',
        ro: 'Albire Intracanală (1 dinte)'
      },
      price: '£150'
    }]
  }, {
    id: 'extraction',
    anchorId: 'extractions',
    title: {
      en: 'Tooth Extraction & Oral Surgery',
      ua: 'Видалення та хірургія',
      ru: 'Удаление и хирургия',
      ro: 'Extracții și Chirurgie Orală'
    },
    items: [{
      name: {
        en: 'Simple Extraction',
        ua: 'Просте видалення',
        ru: 'Простое удаление',
        ro: 'Extracție Simplă'
      },
      price: 'from £120'
    }, {
      name: {
        en: 'Surgical Extraction',
        ua: 'Хірургічне видалення',
        ru: 'Хирургическое удаление',
        ro: 'Extracție Chirurgicală'
      },
      price: 'from £180–250'
    }, {
      name: {
        en: 'Wisdom Tooth Removal',
        ua: 'Видалення зуба мудрості',
        ru: 'Удаление зуба мудрости',
        ro: 'Extracția Molarului de Minte'
      },
      price: 'from £180–350'
    }, {
      name: {
        en: 'Apicectomy',
        ua: 'Апікоектомія',
        ru: 'Апикоэктомия',
        ro: 'Apicectomie'
      },
      price: '£600'
    }, {
      name: {
        en: 'Bone Grafting',
        ua: 'Кісткова пластика',
        ru: 'Костная пластика',
        ro: 'Grefă Osoasă'
      },
      price: 'from £350'
    }, {
      name: {
        en: 'Frenectomy',
        ua: 'Френектомія',
        ru: 'Френэктомия',
        ro: 'Frenectomie'
      },
      price: '£250–350'
    }]
  }, {
    id: 'dentures',
    anchorId: 'dentures',
    title: {
      en: 'Dentures',
      ua: 'Протези',
      ru: 'Протезы',
      ro: 'Proteze Dentare'
    },
    items: [{
      name: {
        en: 'Full Acrylic Denture (one arch)',
        ua: 'Повний акриловий протез (одна щелепа)',
        ru: 'Полный акриловый протез (одна челюсть)',
        ro: 'Proteză Acrilică Totală (o arcadă)'
      },
      price: '£900'
    }, {
      name: {
        en: 'Partial Acrylic Denture',
        ua: 'Частковий акриловий протез',
        ru: 'Частичный акриловый протез',
        ro: 'Proteză Acrilică Parțială'
      },
      price: '£600'
    }, {
      name: {
        en: 'Chrome Cobalt Dentures',
        ua: 'Бюгельний протез',
        ru: 'Бюгельный протез',
        ro: 'Proteze Crom-Cobalt'
      },
      price: '£1100'
    }, {
      name: {
        en: 'Denture Repair',
        ua: 'Ремонт протеза',
        ru: 'Ремонт протеза',
        ro: 'Reparare Proteză'
      },
      price: 'from £250'
    }]
  }, {
    id: 'crowns-veneers',
    anchorId: 'crowns-veneers',
    title: {
      en: 'Crowns, Veneers & Bridges',
      ua: 'Коронки, вініри та мости',
      ru: 'Коронки, виниры и мосты',
      ro: 'Coroane, Fațete și Punți'
    },
    items: [{
      name: {
        en: 'Metal-Ceramic Crown',
        ua: 'Металокерамічна коронка',
        ru: 'Металлокерамическая коронка',
        ro: 'Coroană Metalo-Ceramică'
      },
      price: '£550'
    }, {
      name: {
        en: 'Zirconia Crown',
        ua: 'Цирконієва коронка',
        ru: 'Циркониевая коронка',
        ro: 'Coroană de Zirconiu'
      },
      price: '£600–750'
    }, {
      name: {
        en: 'E.max Veneer',
        ua: 'Вінір E.max',
        ru: 'Винир E.max',
        ro: 'Fațetă E.max'
      },
      price: '£600–750'
    }, {
      name: {
        en: 'Composite Veneer',
        ua: 'Композитний вінір',
        ru: 'Композитный винир',
        ro: 'Fațetă Compozită'
      },
      price: '£350'
    }, {
      name: {
        en: 'Porcelain Bridge (per unit)',
        ua: 'Керамічний міст (1 одиниця)',
        ru: 'Керамический мост (1 единица)',
        ro: 'Punte de Porțelan (per unitate)'
      },
      price: '£600'
    }]
  }, {
    id: 'orthodontics',
    anchorId: 'orthodontics',
    title: {
      en: 'Orthodontics',
      ua: 'Ортодонтія',
      ru: 'Ортодонтия',
      ro: 'Ortodonție'
    },
    items: [{
      name: {
        en: 'Braces',
        ua: 'Брекети',
        ru: 'Брекеты',
        ro: 'Aparat Dentar'
      },
      price: 'from £1800'
    }, {
      name: {
        en: 'Retainers (removable)',
        ua: 'Ретейнери (знімні)',
        ru: 'Ретейнеры (съемные)',
        ro: 'Gutiere de Contenție (mobile)'
      },
      price: '£200'
    }, {
      name: {
        en: 'Night Guard',
        ua: 'Нічна капа',
        ru: 'Ночная капа',
        ro: 'Gutieră de Noapte'
      },
      price: '£200'
    }, {
      name: {
        en: 'Fixed Retainer (per arch)',
        ua: 'Незнімний ретейнер (1 щелепа)',
        ru: 'Несъемный ретейнер (1 челюсть)',
        ro: 'Contenție Fixă (per arcadă)'
      },
      price: '£200'
    }, {
      name: {
        en: 'Clear Aligner',
        ua: 'Прозорі елайнери',
        ru: 'Прозрачные элайнеры',
        ro: 'Aliniere Transparentă'
      },
      price: 'from £3800'
    }]
  }, {
    id: 'radiography',
    anchorId: 'radiography',
    title: {
      en: 'Radiography',
      ua: 'Рентгенографія',
      ru: 'Рентгенография',
      ro: 'Radiografie'
    },
    items: [{
      name: {
        en: 'Small X-ray',
        ua: 'Малий знімок',
        ru: 'Малый снимок',
        ro: 'Radiografie Mică'
      },
      price: '£20'
    }, {
      name: {
        en: 'OPG',
        ua: 'Панорамний знімок (OPG)',
        ru: 'Панорамный снимок (OPG)',
        ro: 'OPG'
      },
      price: '£50'
    }, {
      name: {
        en: 'Cephalometric',
        ua: 'Цефалометричний знімок',
        ru: 'Цефалометрический снимок',
        ro: 'Cefalometrică'
      },
      price: '£50'
    }, {
      name: {
        en: 'CBCT (single jaw)',
        ua: 'КТ (одна щелепа)',
        ru: 'КТ (одна челюсть)',
        ro: 'CBCT (o arcadă)'
      },
      price: '£100'
    }, {
      name: {
        en: 'CBCT (both jaws)',
        ua: 'КТ (обидві щелепи)',
        ru: 'КТ (обе челюсти)',
        ro: 'CBCT (ambele arcade)'
      },
      price: '£150'
    }, {
      name: {
        en: 'CBCT (segment)',
        ua: 'КТ (сегмент)',
        ru: 'КТ (сегмент)',
        ro: 'CBCT (segment)'
      },
      price: '£80'
    }]
  }, {
    id: 'indirect',
    anchorId: 'indirect',
    title: {
      en: 'Indirect Restorations',
      ua: 'Непрямі реставрації',
      ru: 'Непрямые реставрации',
      ro: 'Restaurări Indirecte'
    },
    items: [{
      name: {
        en: 'Ceramic Inlay',
        ua: 'Керамічна вкладка',
        ru: 'Керамическая вкладка',
        ro: 'Inlay Ceramic'
      },
      price: '£400'
    }, {
      name: {
        en: 'Ceramic Onlay',
        ua: 'Керамічна накладка',
        ru: 'Керамическая накладка',
        ro: 'Onlay Ceramic'
      },
      price: '£450'
    }, {
      name: {
        en: 'Ceramic Overlay',
        ua: 'Керамічний оверлей',
        ru: 'Керамический оверлей',
        ro: 'Overlay Ceramic'
      },
      price: '£500'
    }]
  }, {
    id: 'implants',
    anchorId: 'implants',
    title: {
      en: 'Dental Implants',
      ua: 'Імплантація',
      ru: 'Имплантация',
      ro: 'Implanturi Dentare'
    },
    items: [{
      name: {
        en: 'Implant with Crown',
        ua: 'Імплант з коронкою',
        ru: 'Имплант с коронкой',
        ro: 'Implant cu Coroană'
      },
      price: 'from £1500'
    }, {
      name: {
        en: 'Sinus Lift',
        ua: 'Синус-ліфтинг',
        ru: 'Синус-лифтинг',
        ro: 'Sinus Lift'
      },
      price: 'from £800'
    }]
  }, {
    id: 'aesthetic',
    anchorId: 'injectables',
    title: {
      en: 'Injectables & Aesthetic',
      ua: "Ін'єкційна косметологія",
      ru: 'Инъекционная косметология',
      ro: 'Injectabile și Estetică'
    },
    items: [{
      name: {
        en: 'Botox',
        ua: 'Ботокс',
        ru: 'Ботокс',
        ro: 'Botox'
      },
      price: 'from £250'
    }, {
      name: {
        en: 'Hydrafacial',
        ua: 'Гідрафейшл',
        ru: 'Гидрафейшл',
        ro: 'Hydrafacial'
      },
      price: '£250'
    }]
  }];
  // Robust hash-based scroll — retries until element is found (mobile timing fix)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) {
      hasScrolledRef.current = false;
      return;
    }
    const matchedCategory = categories.find(cat => cat.anchorId === hash);
    if (!matchedCategory) return;
    // Open the accordion and highlight
    setOpenId(matchedCategory.id);
    setHighlightedAnchor(matchedCategory.anchorId);
    // Scroll to element with retry logic for mobile
    const HEADER_OFFSET = 100; // sticky header height + padding
    let attempts = 0;
    const maxAttempts = 20; // retry for ~2 seconds
    const tryScroll = () => {
      const el = document.getElementById(hash);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
        hasScrolledRef.current = true;
        return true;
      }
      return false;
    };
    // Try immediately
    if (tryScroll()) {
      // Also re-scroll after a beat for layout shifts
      setTimeout(tryScroll, 300);
    } else {
      // Retry with interval
      const interval = setInterval(() => {
        attempts++;
        if (tryScroll() || attempts >= maxAttempts) {
          clearInterval(interval);
          // One more attempt after accordion animation
          if (attempts < maxAttempts) {
            setTimeout(tryScroll, 350);
          }
        }
      }, 100);
      return () => clearInterval(interval);
    }
    // Remove highlight after 2.5s
    const highlightTimer = setTimeout(() => {
      setHighlightedAnchor(null);
    }, 2500);
    return () => clearTimeout(highlightTimer);
  }, [location.hash, location.key]);
  const midpoint = Math.ceil(categories.length / 2);
  const leftColumn = categories.slice(0, midpoint);
  const rightColumn = categories.slice(midpoint);
  return <Layout data-id="element-2112">
      {/* Hero */}
      <section className="bg-teal-900 py-16 lg:py-20 text-white" data-id="element-2113">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2114">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-2115">
            {t('prices.title')}
          </h1>
          <p className="text-lg text-teal-100 max-w-2xl mx-auto" data-id="element-2116">
            {t('prices.subtitle')}
          </p>
        </div>
      </section>

      {/* Accordion Price List */}
      <section className="py-12 lg:py-20 bg-gray-50" data-id="element-2117">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2118">
          <div className="grid lg:grid-cols-2 gap-x-10 gap-y-0" data-id="element-2119">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 sm:px-8 py-2 mb-4 lg:mb-0" data-id="element-2120">
              {leftColumn.map(cat => <AccordionItem key={cat.id} category={cat} isOpen={openId === cat.id} onToggle={() => toggle(cat.id)} lang={lang as 'en' | 'ua' | 'ru' | 'ro'} isHighlighted={highlightedAnchor === cat.anchorId} data-id="element-2121" />)}
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 sm:px-8 py-2" data-id="element-2122">
              {rightColumn.map(cat => <AccordionItem key={cat.id} category={cat} isOpen={openId === cat.id} onToggle={() => toggle(cat.id)} lang={lang as 'en' | 'ua' | 'ru' | 'ro'} isHighlighted={highlightedAnchor === cat.anchorId} data-id="element-2123" />)}
            </div>
          </div>

          {/* Note */}
          <p className="mt-10 text-center text-gray-500 text-sm max-w-2xl mx-auto" data-id="element-2124">
            {t('prices.note')}
          </p>

          {/* Footer note */}
          <p className="mt-2 text-center text-gray-400 text-xs max-w-2xl mx-auto" data-id="element-2125">
            {lang === 'en' ? 'Prices may vary. Consultation required.' : lang === 'ua' ? 'Ціни можуть відрізнятися. Необхідна консультація.' : lang === 'ro' ? 'Prețurile pot varia. Este necesară o consultație.' : 'Цены могут отличаться. Необходима консультация.'}
          </p>

          {/* CTA Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3" data-id="element-2126">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2127">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2128">
                <Calendar className="w-5 h-5" data-id="element-2129" />
                {t('prices.bookCta')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-2130">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2131">
                <MessageCircle className="w-5 h-5" data-id="element-2132" />
                WhatsApp
              </Button>
            </a>
            <Link to="/price-list" target="_blank" rel="noopener noreferrer" data-id="element-2133">
              <Button variant="secondary" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2134">
                <Download className="w-5 h-5" data-id="element-2135" />
                {lang === 'en' ? 'Print Price List' : lang === 'ua' ? 'Роздрукувати прайс' : lang === 'ro' ? 'Imprimare Prețuri' : 'Распечатать прайс'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Link */}
      <section className="py-16 bg-white" data-id="element-2136">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-2137">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2138">
            {t('services.title')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-2139">{t('services.subtitle')}</p>
          <Link to={`/${lang}/services`} data-id="element-2140">
            <Button variant="secondary" size="lg" data-id="element-2141">
              {t('services.viewAll')}
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}